import { add, differenceInDays, startOfDay, startOfHour, sub } from 'date-fns';
import { useContext, useEffect, useRef, useState } from 'react';
import { Route, useHistory, useLocation, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { BackpressureIssueWithData, DeliveryIssueWithData, RequestIssue as RequestIssueType, TransformationIssue as TransformationIssueType } from '../../../../../../../typings/Issue.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { TransformationExecutionsQueryProps } from '../../../../typings/TransformationExecution.interface';
import { extractFromArray } from '../../../../utils';
import { RelativeDate, resolveRelativeDate } from '../../../../utils/date';
import { issue_actions, ISSUE_STATUS_CONFIGS } from '../../../../utils/issues';
import { isRejectionCauseRetryable } from '../../../../utils/rejection-causes';
import Button, { ButtonGroup } from '../../../common/base/Button';
import Icon from '../../../common/base/Icon';
import Loading from '../../../common/base/Loading';
import Tabs from '../../../common/base/Tabs';
import { BulkOperationDropdown } from '../../../common/BulkOperations';
import { Div } from '../../../common/helpers/StyledUtils';
import { useToasts } from '../../../common/Toast';
import { GlobalContext } from '../../../contexts/GlobalContext';
import useSearchQuery from '../../../hooks/useSearchQuery';
import useTransformationExecutionsList from '../../../hooks/useTransformationExecutionsList';
import { StyledViewContent, StyledViewLayout, StyledViewNav, StyledViewNavActions, StyledViewNavHeader, StyledViewNavSecondary, ViewBreadcrumbsTitle } from '../../../layouts/ViewLayout';
import { DashboardContext } from '../DashboardContext';
import { DestinationDeliveryGroups } from '../Connections/Destination/DestinationDeliveryGroups';
import { RefreshKeyProvider } from '../Metrics/RefreshKeyContext';
import NotFound from '../NotFound';
import TransformationExecutionDetails from '../Transformation/TransformationExecutionDetails';
import { IssueOverview } from './IssueOverview';
import { IssueTransformationExecutions } from './IssueTransformationExecutions';
const IssueView: React.FC = () => {
  const {
    id: issue_id
  } = useParams<{
    id: string;
  }>();
  const location = useLocation();
  const history = useHistory();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    subscription
  } = useContext(DashboardContext);
  const {
    data: issue,
    mutate,
    error
  } = useSWR(APIMethodKeys.issues.get(issue_id), () => HookdeckAPI.issues.get(issue_id));
  const {
    current: now
  } = useRef(new Date());
  const [refresh_key, setRefreshKey] = useState(new Date().getTime());
  const [resolve_action_applied, setResolveActionApplied] = useState(false);
  const {
    query,
    updateSearchQuery
  } = useSearchQuery<{
    date?: {
      relative: RelativeDate;
      min: string;
      max: string;
    };
    tab?: string;
  } & TransformationExecutionsQueryProps>();
  const setDate = date => updateSearchQuery({
    date
  });
  const selected_execution_id = extractFromArray(query.selected_execution_id) as string;
  const {
    addToast
  } = useToasts();
  const {
    data: webhooks
  } = useSWR(issue && issue.type === 'delivery' && APIMethodKeys.webhooks.list({
    id: issue.aggregation_keys.webhook_id
  }), () => HookdeckAPI.webhooks.list({
    id: (issue as DeliveryIssueWithData).aggregation_keys.webhook_id
  }));
  const {
    data: destinations
  } = useSWR(issue && issue.type === 'backpressure' && APIMethodKeys.destinations.list({
    id: issue.aggregation_keys.destination_id
  }), () => HookdeckAPI.destinations.list({
    id: (issue as BackpressureIssueWithData).aggregation_keys.destination_id
  }));
  const {
    data: transformations
  } = useSWR(issue && issue.type === 'transformation' && APIMethodKeys.transformations.list({
    id: issue.aggregation_keys.transformation_id
  }), () => HookdeckAPI.transformations.list({
    id: (issue as TransformationIssueType).aggregation_keys.transformation_id
  }));
  const {
    data: sources
  } = useSWR(issue && issue.type === 'request' && APIMethodKeys.sources.list({
    id: issue.aggregation_keys.source_id
  }), () => HookdeckAPI.sources.list({
    id: (issue as RequestIssueType).aggregation_keys.source_id
  }));
  const {
    count: fatal_executions_count
  } = useTransformationExecutionsList((issue as TransformationIssueType)?.aggregation_keys?.transformation_id?.[0], {
    issue_id: issue?.id,
    log_level: ['fatal']
  });
  const webhooks_by_id = webhooks?.models.reduce((object, webhook) => ({
    ...object,
    [webhook.id]: webhook
  }), {});
  const destinations_by_id = destinations?.models.reduce((object_0, destination) => ({
    ...object_0,
    [destination.id]: destination
  }), {});
  const transformations_by_id = transformations?.models.reduce((object_1, transformation) => ({
    ...object_1,
    [transformation.id]: transformation
  }), {});
  const sources_by_id = sources?.models.reduce((object_2, source) => ({
    ...object_2,
    [source.id]: source
  }), {});
  useEffect(() => {
    let interval;
    if (resolve_action_applied) {
      setRefreshKey(new Date().getTime());
      interval = setInterval(() => {
        setRefreshKey(new Date().getTime());
      }, 10000);
    }
    return () => interval && clearInterval(interval);
  }, [resolve_action_applied]);
  const applyAction = async (action: keyof typeof issue_actions) => {
    let promise;
    if (action === 'acknowledge') {
      promise = HookdeckAPI.issues.update(issue_id, {
        status: 'ACKNOWLEDGED'
      });
    } else if (action === 'resolve') {
      promise = HookdeckAPI.issues.update(issue_id, {
        status: 'RESOLVED'
      });
    } else if (action === 'reopen') {
      promise = HookdeckAPI.issues.update(issue_id, {
        status: 'OPENED'
      });
    } else if (action === 'ignore') {
      promise = HookdeckAPI.issues.update(issue_id, {
        status: 'IGNORED'
      });
    } else if (action === 'dismiss') {
      promise = HookdeckAPI.issues.dismiss(issue_id);
    }
    promise.then(() => {
      mutate();
      addToast('success', 'Issue status updated');
    }).catch(() => addToast('error', 'Failed to update issue'));
  };
  if (error && error.response?.status === 404) {
    return <NotFound title="Issue not found" description={`This issue does not exist within this project.`} id={issue_id} link={{
      to: `/issues`,
      text: 'All Issues'
    }} />;
  }
  if (!issue || issue.type === 'delivery' && !webhooks_by_id || issue.type === 'transformation' && !transformations_by_id || issue.type === 'backpressure' && !destinations_by_id || issue.type === 'request' && !sources_by_id) {
    return <Div flex={{
      justify: 'center'
    }} p={8}>
        <Loading />
      </Div>;
  }
  let start_date = query.date?.min ? new Date(query.date.min) : differenceInDays(new Date(), new Date(issue.first_seen_at)) > subscription!.retention_days ? startOfDay(sub(new Date(), {
    days: subscription!.retention_days
  })) : startOfDay(new Date(issue.first_seen_at)) as Date;
  let end_date = query.date?.max ? new Date(query.date.max) : add(startOfHour(now), {
    hours: 1
  });
  if (query.date?.relative) {
    const date_0 = resolveRelativeDate(query.date.relative);
    start_date = date_0.min;
    end_date = date_0.max;
  }
  const render_data = {
    webhooks_by_id,
    transformations_by_id,
    destinations_by_id,
    sources_by_id
  };
  const {
    default_action,
    current_action
  } = ISSUE_STATUS_CONFIGS[issue.status];
  const is_fatal_executions_tab = location.pathname.endsWith('/executions');
  const is_delivery_groups_tab = location.pathname.endsWith('/delivery-groups');
  const current_tab = is_fatal_executions_tab ? 'executions' : is_delivery_groups_tab ? 'delivery-groups' : 'overview';
  const backpressure_delivery_groups = issue.type === 'backpressure' ? issue.reference.delivery_groups || [] : [];
  const TABS = {
    OVERVIEW: {
      label: 'Overview',
      key: 'overview',
      to: `/issues/${issue_id}`
    },
    FATAL_EXECUTIONS: {
      label: 'Executions',
      key: 'executions',
      badge_label: fatal_executions_count,
      badge_theme: 'muted',
      to: `/issues/${issue_id}/executions`
    },
    DELIVERY_GROUPS: {
      label: 'Delivery groups',
      key: 'delivery-groups',
      badge_label: backpressure_delivery_groups.length,
      badge_theme: 'danger',
      to: `/issues/${issue_id}/delivery-groups`
    }
  } as const;
  const tabs = [TABS.OVERVIEW, ...(issue.type === 'transformation' ? [TABS.FATAL_EXECUTIONS] : []), ...(issue.type === 'backpressure' && backpressure_delivery_groups.length > 0 ? [TABS.DELIVERY_GROUPS] : [])];
  const has_secondary_nav = tabs.length > 1;
  return <StyledViewLayout>
      <StyledViewContent>
        <StyledViewNav>
          <StyledViewNavHeader>
            <ViewBreadcrumbsTitle breadcrumbs={[{
            label: 'Issues',
            to: '/issues'
          }, {
            label: issue_id,
            highlight: true
          }]} />
            <StyledViewNavActions>
              <ButtonGroup more_options={Object.keys(issue_actions).filter(key => {
              if (key === default_action || key === current_action) {
                return false;
              }
              if (key === 'dismiss' && issue.dismissed_at) {
                return null;
              }
              return issue.status === 'OPENED' ? key !== 'reopen' : true;
            }).map((key_0: keyof typeof issue_actions) => ({
              label: issue_actions[key_0].label,
              danger: issue_actions[key_0].danger,
              icon: issue_actions[key_0].icon,
              onClick: () => applyAction(key_0)
            }))}>
                <Button.Permission outline onClick={() => applyAction(default_action)}>
                  <Icon icon={issue_actions[default_action].icon} muted left />
                  {issue_actions[default_action].label}
                </Button.Permission>
              </ButtonGroup>
              {issue && issue.type === 'transformation' && issue.aggregation_keys.log_level.includes('fatal') && <BulkOperationDropdown operation_type="retry" model="ignored_events" filters={{
              transformation_id: issue.aggregation_keys.transformation_id[0],
              cause: ['TRANSFORMATION_FAILED'],
              issue_id: issue.id
            }} onSubmit={() => setResolveActionApplied(true)} />}
              {issue && issue.type === 'transformation' && issue.aggregation_keys.log_level.includes('fatal') && <Button icon="edit" outline to={`/transformations/${issue.aggregation_keys.transformation_id[0]}/edit?issue_id=${issue_id}`}>
                    Edit transformation
                  </Button>}
              {issue && issue.type === 'delivery' && <BulkOperationDropdown operation_type="retry" model="events" filters={{
              ...issue.aggregation_keys,
              response_status: issue.aggregation_keys.response_status.map(status => `${status}`),
              status: ['failed'],
              date: {
                min: start_date.toISOString(),
                max: end_date.toISOString()
              },
              issue_id: issue.id
            }} onSubmit={() => setResolveActionApplied(true)} />}
              {issue && issue.type === 'request' && issue.aggregation_keys.rejection_cause.some(cause => isRejectionCauseRetryable(cause)) && <BulkOperationDropdown operation_type="retry" model="requests" filters={{
              ...issue.aggregation_keys
            }} onSubmit={() => setResolveActionApplied(true)} />}
            </StyledViewNavActions>
          </StyledViewNavHeader>
          {has_secondary_nav && <StyledViewNavSecondary>
              <Tabs active_tab={current_tab} onTabSelected={tab => updateSearchQuery({
            tab
          })} border={false} tabs={tabs} />
            </StyledViewNavSecondary>}
        </StyledViewNav>
        <Route exact path="/issues/:id">
          <RefreshKeyProvider>
            <IssueOverview issue={issue} start_date={start_date} end_date={end_date} relative_date={query.date?.relative} setDate={setDate} refresh_key={String(refresh_key)} render_data={render_data} mutateIssue={mutate} />
          </RefreshKeyProvider>
        </Route>
        {issue.type === 'transformation' && <Route exact path="/issues/:id/executions">
            <IssueTransformationExecutions issue={issue} fatal_executions={fatal_executions_count} mutateFatalExecutions={() => setRefreshKey(new Date().getTime())} onEdit={execution => {
          history.push(`/transformations/${issue.aggregation_keys.transformation_id[0]}/edit?input_execution_id=${execution?.id}&issue_id=${issue_id}`);
        }} />
          </Route>}
        {issue.type === 'backpressure' && backpressure_delivery_groups.length > 0 && <Route exact path="/issues/:id/delivery-groups">
            <RefreshKeyProvider>
              <DestinationDeliveryGroups destination={issue.data.destination} allowed_delivery_groups={backpressure_delivery_groups} mutate={() => mutate()} />
            </RefreshKeyProvider>
          </Route>}
      </StyledViewContent>
      {selected_execution_id && issue.type === 'transformation' && <TransformationExecutionDetails transformation_id={issue.aggregation_keys.transformation_id[0]} execution_id={selected_execution_id} onClose={() => updateSearchQuery({}, {
      remove_keys: ['selected_execution_id']
    })} />}
    </StyledViewLayout>;
};
export default IssueView;